import React, {forwardRef, MouseEvent, useEffect, useImperativeHandle, useRef, useState} from 'react'
import {IconButton, Popover, Row} from '../index.js'
import {FieldProps, FieldRef, InvalidProps} from './types.js'
import FieldBox, {FieldCaret} from './FieldBox.js'
import {useFieldId} from './utils.js'
import type {PopoverRef} from '../overlays/Popover.js'

// The click event of the chosen icon button, with target.value set to the icon name (without mdi- prefix)
export interface IconPickerChangeEvent {
    target: {value: string}
}

export interface IconPickerProps extends FieldProps, InvalidProps {
    id?: string
    value: string
    icons: string[]
    onChange?: (event: IconPickerChangeEvent) => void
}

export type IconPickerRef = FieldRef

const IconPicker = forwardRef<IconPickerRef, IconPickerProps>(function IconPicker({id, value, icons, onChange, label, info, disabled, readOnly, isInvalid, style}, ref) {
    const [popOverOpen, setPopOverOpen] = useState(false)
    const [isInvalidState, setIsInvalidState] = useState(false)

    const popoverRef = useRef<PopoverRef>(null)
    const fieldRef = useRef<HTMLDivElement>(null)
    const blurTimeout = useRef<ReturnType<typeof setTimeout>>(null)

    const fieldId = useFieldId(id)
    // The box is a div, not labelable: it points at the label with aria-labelledby, clicking the label focuses it
    const labelId = `${fieldId}-label`

    useEffect(() => {
        return () => clearTimeout(blurTimeout.current)
    }, [])

    useImperativeHandle(ref, () => ({
        focus: () => fieldRef.current?.focus(),
        blur: () => fieldRef.current?.blur(),
        setInvalid: (isInvalid = true) => {
            setIsInvalidState(isInvalid)
            fieldRef.current?.focus()
        }
    }), [])

    const onClickIcon = (icon: string, event: MouseEvent) => {
        if (disabled || readOnly) {
            return
        }

        setIsInvalidState(false)
        const changeEvent = event as unknown as IconPickerChangeEvent
        changeEvent.target.value = icon
        onChange?.(changeEvent)
    }

    const invalid = isInvalid || isInvalidState

    // Focused while the popover is open: it opens on focus and closes on blur, and stays open while an icon is clicked
    const focused = popOverOpen && !disabled && !readOnly

    const focusedState = focused || value

    const show = () => {
        if (!disabled && !readOnly) {
            popoverRef.current?.show()
        }
    }

    return (
        <FieldBox
            label={label}
            info={info}
            floated={!!focusedState}
            focused={focused}
            invalid={invalid}
            disabled={disabled}
            readOnly={readOnly}
            // The caret
            trailingWidth={20}
            labelId={label ? labelId : undefined}
            onLabelClick={() => fieldRef.current?.focus()}
            style={style}
            boxRef={fieldRef}
            boxProps={{
                // readOnly stays focusable like a read-only text field, disabled is skipped
                'id': fieldId,
                'tabIndex': disabled ? -1 : 0,
                'role': 'button',
                'aria-haspopup': true,
                'aria-disabled': disabled || readOnly || undefined,
                'aria-invalid': invalid ? true : undefined,
                'aria-labelledby': label ? labelId : undefined,
                'aria-label': label ? undefined : 'Icoon kiezen',
                // Opens on focus, a click opens it again after a click outside closed it while focused
                'onFocus': show,
                'onClick': show,
                'onBlur': () => {
                    clearTimeout(blurTimeout.current)
                    blurTimeout.current = setTimeout(() => {
                        popoverRef.current?.close()
                    }, 100)
                }
            }}
        >
            <Row style={{flexShrink: 0, position: 'absolute', left: 12, bottom: 6, paddingRight: 20}}>
                <i style={{fontSize: 18}} className={`mdi mdi-${value}`} />
            </Row>

            <FieldCaret disabled={disabled} readOnly={readOnly} />

            {/* Without children it renders nothing in the box, the icons open at the box (in a portal on the body) */}
            <Popover
                ref={popoverRef}
                anchorRef={fieldRef}
                disabled={disabled || readOnly}
                content={
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 4, width: 'auto'}}>
                        {icons.map((icon) => (
                            <IconButton
                                key={icon}
                                aria-label={icon}
                                onClick={(event) => onClickIcon(icon, event)}
                            >
                                <i style={{fontSize: 20}} className={`mdi mdi-${icon}`} />
                            </IconButton>
                        ))}
                    </div>
                }
                onToggle={(open: boolean) => setPopOverOpen(open)}
            />
        </FieldBox>
    )
})

export default IconPicker
